/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate,
  Navigate,
} from "react-router-dom";
import Login from "./pages/Login/login";
import Dashboard from "./pages/Dashboard/dashboard";
import OrderManagement from "./pages/OrderManagement/order-management";
import Location from "./pages/Location/location";
import Header from "./components/Header";
import { Toaster } from "react-hot-toast";
import { useEffect, useRef, useState } from "react";
import { useProfileData } from "./apis/profileApis";
import ForgotPassword from "./pages/Forgot-password/ForgotPassword";
import ChangePassword from "./pages/ChangePassword/ChangePassword";
import LineHaulManagement from "./pages/LineHaulManagement/line-haul-management";
import Settings from "./pages/Settings/settings";
import Integration from "./pages/Integration/integration";
import LoadingScreen from "./components/common/LoadingScreen";
import Profile from "./pages/Profile/profile";
import GlobalTooltipPortal from "./components/common/TooltipPortal";
import PickupManagement from "./pages/PickupManagement/pickup-management";
import ManifestManagement from "./pages/ManifestManagement/manifest-management";
import InvoiceManagement from "./pages/InvoiceManagement/invoice-management";
import ZoneManagement from "./pages/ZoneManagement/zone-management";
import BarcodeQrGenerator from "./pages/BarcodeQrGenerator";
import WebhooksPage from "./pages/Webhooks";
import HelpandSupport from "./pages/HelpandSupport/help-and-support";
import ViewShipmentDetails from "./pages/PickupManagement/ViewShipmentDetails";
import { initSocket } from "./socket";
import { useQueryClient } from "@tanstack/react-query";
import InverntoryProduct from "./pages/InventoryManagement/inverntory-product";
import ProductDemo from "./pages/ProductDemo/product-demo";
import AddItem from "./pages/InventoryManagement/add-item";
import Notification from "./pages/Notification/notification";
import NotificationView from "./pages/Notification/notificationView";
import ViewOrderDetails from "./pages/OrderManagement/ViewOrderDetails";
import {
  addNewOrder,
  addSingleStatusUpdate,
} from "./features/orderStatusHistories/ordersStatusHistoriesSlice";
import { useDispatch } from "react-redux";
import { useIdleTimeout } from "./hooks/useIdleTimeout";

function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data, isSuccess } = useProfileData();
  const queryClient = useQueryClient();
  const socketRef = useRef<any>(null); // single socket instance
  const [socketConnected, setSocketConnected] = useState(false);
  const [unseenNotificationCount, setUnseenNotificationCount] = useState(0);

  const allHideHeaderRoutes = ["/", "/forgot-password", "/password-changed"];
  const hideHeader = allHideHeaderRoutes.includes(location.pathname);

  // Handle redirects after checking profile
  useEffect(() => {
    if (!isSuccess) return;

    if (!data || data === "Invalid or expired token") {
      // Not logged in
      if (!allHideHeaderRoutes.includes(location.pathname)) {
        navigate("/", { replace: true });
      }
    } else {
      // Logged in
      if (location.pathname === "/") {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [data, isSuccess, location.pathname]);

  const isAuthenticated = data && data !== "Invalid or expired token";

  useIdleTimeout(!!isAuthenticated);

  const user = data;

  const isExternalAdmin = process.env.REACT_APP_EXTERNAL_ADMIN === "true";

  useEffect(() => {
    // Update title
    document.title = isExternalAdmin ? "Najiz Express" : "tracktechs";

    // Update favicon
    let link = document.querySelector(
      "link[rel*='icon']",
    ) as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      document.head.appendChild(link);
    }
    link.rel = "icon";
    link.href = isExternalAdmin ? "/logo/najizFavicon.png" : "/favicon.ico";
  }, [isExternalAdmin]);

  // Socket setup (single instance)
  useEffect(() => {
    if (
      process.env.REACT_APP_Socket === "true" &&
      user?.id &&
      !socketRef.current
    ) {
      socketRef.current = initSocket();
      const socket = socketRef.current;

      console.log("🔌 Initializing socket:", socket);

      socket.on("connect", () => {
        console.log("✅ Socket Connected!", socket.id);
        setSocketConnected(true);
        socket.emit("setup", { id: user.id });
      });

      socket.on("disconnect", () => {
        console.log("❌ Socket Disconnected!");
        setSocketConnected(false);
      });

      socket.on("location received", (data: any) => {
        console.log("📍 Location received:", data);
      });

      socket.on("notification Received", (data: any) => {
        console.log("🔔 Notification received:", data);
        setUnseenNotificationCount(data?.unseenNotification || 0);
      });

      socket.on("new-order-status", (data: any) => {
        console.log("🔔 new-order-status:", data);
        dispatch(addSingleStatusUpdate(data));
        // toast.success(`New Status Of Order ${data?.awbNumber}`);
      });

      socket.on("new-order", (data: any) => {
        console.log("🔔 new-order:", data);
        dispatch(addNewOrder(data));
        // toast.success(`New Order ${data?.awbNumber}`);
      });

      socket.on("backend-change", (data: { model: string; action: string }) => {
        console.log("🔄 Backend change received:", data);

        // 1. Only track UI-relevant models
        const trackedModels = [
          "Order", "OrderStatusHistory", "Driver", "Staff", "StaffDetails",
          "ClientDetails", "LmClient", "DarkStore", "PickupRequest",
          "LineHaulRequest", "Vehicle", "Warehouse", "AdminNotifications",
          "ChatMessage", "ChatRoom", "PickList", "ProductManifest",
          "LinehaulManifest", "ShippingPartnerManifest", "ReturnToShipper"
        ];
        if (!trackedModels.includes(data.model)) {
          return;
        }

        // 2. Prevent invalidations if the user is typing or if any active edit/add form is open
        // 2. Prevent invalidations if the user is typing or if any active edit/add form is open
        const activeElement = document.activeElement;
        const isTyping = activeElement && ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName);

        const hasActiveForm = Array.from(document.querySelectorAll("input, textarea, select")).some(el => {
          const tagName = el.tagName;
          const type = el.getAttribute("type");
          const placeholder = el.getAttribute("placeholder")?.toLowerCase() || "";
          const id = el.id?.toLowerCase() || "";
          const name = el.getAttribute("name")?.toLowerCase() || "";
          const className = el.className?.toLowerCase() || "";

          // Ignore checkboxes, radio buttons, hidden inputs, files, buttons
          const isIgnoredInput = type && ["checkbox", "radio", "hidden", "file", "button", "submit", "reset"].includes(type);
          if (isIgnoredInput) return false;

          // Ignore search fields
          const isSearch = type === "search" ||
            placeholder.includes("search") ||
            id.includes("search") ||
            name.includes("search") ||
            className.includes("search");
          if (isSearch) return false;

          // Ignore pagination selects / limit selectors
          const isPagination = tagName === "SELECT" && (
            name.includes("limit") ||
            name.includes("page") ||
            name.includes("size") ||
            id.includes("limit") ||
            id.includes("page") ||
            id.includes("size") ||
            className.includes("limit") ||
            className.includes("page") ||
            className.includes("size")
          );
          if (isPagination) return false;

          // Ignore filter & sort selectors
          const isFilterOrSort = name.includes("filter") ||
            name.includes("sort") ||
            id.includes("filter") ||
            id.includes("sort") ||
            className.includes("filter") ||
            className.includes("sort") ||
            placeholder.includes("filter") ||
            placeholder.includes("sort");
          if (isFilterOrSort) return false;

          // Ignore disabled / readonly
          const isDisabled = el.hasAttribute("disabled") || el.hasAttribute("readonly");
          if (isDisabled) return false;

          // Check if it is inside a form or a modal/sidebar/drawer/popup/overlay container
          let parent = el.parentElement;
          let insideFormOrModal = false;
          while (parent) {
            const pTagName = parent.tagName;
            const pRole = parent.getAttribute("role") || "";
            const pId = parent.id || "";
            const pClassName = typeof parent.className === "string" ? parent.className : "";

            if (pTagName === "FORM" || pTagName === "DIALOG") {
              insideFormOrModal = true;
              break;
            }
            if (pRole === "dialog" || pRole === "alertdialog") {
              insideFormOrModal = true;
              break;
            }
            const lowerId = pId.toLowerCase();
            const lowerClass = pClassName.toLowerCase();
            if (
              lowerId.includes("modal") || lowerId.includes("dialog") || lowerId.includes("drawer") || lowerId.includes("sidebar") || lowerId.includes("popup") ||
              lowerClass.includes("modal") || lowerClass.includes("dialog") || lowerClass.includes("drawer") || lowerClass.includes("sidebar") || lowerClass.includes("popup") ||
              (lowerClass.includes("fixed") && lowerClass.includes("z-"))
            ) {
              insideFormOrModal = true;
              break;
            }
            parent = parent.parentElement;
          }

          if (!insideFormOrModal) return false;

          // If we reach here, it's an active text/number/email/select/textarea form field inside a form/modal
          return true;
        });

        const isEditingPath = /[\/(]edit|install|connect|add|create|fill-info/i.test(window.location.pathname);
        if (isTyping || hasActiveForm || isEditingPath) {
          console.log("⏳ Active form or typing detected. Skipping real-time refresh to protect user inputs.");
          return;
        }

        // 3. Only refresh if the current page/path is relevant to the model
        const path = window.location.pathname;
        const modelToPaths: Record<string, RegExp[]> = {
          Order: [/^\/orders/, /^\/dashboard/, /^\/packaging/, /^\/dispatched/, /^\/created-orders/, /^\/pending-order/, /^\/pickup-management/, /^\/line-haul-management/, /^\/manifest/],
          OrderStatusHistory: [/^\/orders/, /^\/dashboard/, /^\/packaging/, /^\/dispatched/, /^\/created-orders/, /^\/pending-order/, /^\/pickup-management/, /^\/line-haul-management/, /^\/manifest/],
          PickList: [/^\/orders/, /^\/pick-list/, /^\/dashboard/],
          Driver: [/^\/users/, /^\/driver/, /^\/location/, /^\/fleet-management/],
          Staff: [/^\/users/, /^\/staff/],
          StaffDetails: [/^\/users/, /^\/staff/],
          ClientDetails: [/^\/users/, /^\/view-merchant-details/],
          LmClient: [/^\/users/, /^\/view-merchant-details/],
          DarkStore: [/^\/dark-store/],
          PickupRequest: [/^\/pickup-management/, /^\/dashboard/, /^\/manifest/],
          LineHaulRequest: [/^\/line-haul-management/, /^\/manifest/, /^\/dashboard/],
          Vehicle: [/^\/fleet-management/, /^\/view-vehicle-details/, /^\/users/, /^\/driver/, /^\/dashboard/],
          Warehouse: [/^\/warehouse/],
          AdminNotifications: [/.*/],
          ChatMessage: [/^\/help-and-support/],
          ChatRoom: [/^\/help-and-support/],
          ProductManifest: [/^\/manifest/, /^\/dashboard/],
          LinehaulManifest: [/^\/manifest/, /^\/pickup-management/, /^\/line-haul-management/, /^\/dashboard/],
          ShippingPartnerManifest: [/^\/manifest/, /^\/dashboard/],
          ReturnToShipper: [/^\/manifest/, /^\/dashboard/],
        };

        const paths = modelToPaths[data.model];
        const isPathRelevant = paths && paths.some(regex => regex.test(path));
        if (!isPathRelevant) {
          console.log(`⏭️ Change in ${data.model} is not relevant to ${path}. Skipping.`);
          return;
        }

        // 4. Invalidate the query keys
        const modelToQueryKey: Record<string, string[]> = {
          Order: [
            "orders", "order-details", "order", "orderOperations", "ordersData",
            "routeOrdersData", "OrderDetails", "OrderDetailsStatusHistory",
            "deleteOrdersData", "orderShipmentsData", "shipmentsViewData", "orderLineItemsStock",
            "packagingData", "todayPackedStream", "packagingStaff", "picklists", "picklist",
            "unifiedManifest"
          ],
          OrderStatusHistory: [
            "orders", "order-details", "order", "orderOperations", "ordersData",
            "routeOrdersData", "OrderDetails", "OrderDetailsStatusHistory",
            "deleteOrdersData", "orderShipmentsData", "shipmentsViewData", "orderLineItemsStock",
            "packagingData", "todayPackedStream", "packagingStaff", "picklists", "picklist",
            "unifiedManifest"
          ],
          PickList: [
            "picklists", "picklist", "orders", "order-details", "order", "ordersData",
            "OrderDetails", "OrderDetailsStatusHistory"
          ],
          Driver: ["drivers", "driver-details", "driver", "admin-drivers"],
          Staff: ["staffs", "staff-details", "staff", "admin-staff"],
          StaffDetails: ["staffs", "staff-details", "staff", "admin-staff"],
          ClientDetails: ["clients", "client-details", "client"],
          LmClient: ["clients", "client-details", "client"],
          DarkStore: ["dark-stores", "darkStores", "dark-store"],
          PickupRequest: ["pickups", "pickup-details", "pickup", "unifiedManifest"],
          LineHaulRequest: ["linehaul", "linehauls", "line-haul", "unifiedManifest"],
          Vehicle: [
            "vehicles", "vehicle-details", "vehicle", "VehicleList",
            "fleetCardsSummary", "vehiclesData", "vehicleAllData", "assignVehicleAllData"
          ],
          Warehouse: ["warehouses", "warehouse-details", "warehouse"],
          AdminNotifications: ["admin-notifications", "notifications"],
          ChatMessage: ["chat-messages", "chat-rooms", "chats"],
          ChatRoom: ["chat-rooms", "chats"],
          ProductManifest: ["unifiedManifest", "itemProductData", "itemProductViewData", "orders", "order-details", "order", "ordersData"],
          LinehaulManifest: ["unifiedManifest", "linehaulManifest", "linehaulManifestData", "linehaulShipments", "orders", "order-details", "order", "ordersData"],
          ShippingPartnerManifest: ["unifiedManifest", "shipping-partner-manifest", "shipping-partner-manifest-details", "orders", "order-details", "order", "ordersData"],
          ReturnToShipper: ["unifiedManifest", "orders", "order-details", "order", "ordersData"],
          InvoiceManagements: ["invoiceData", "invoices"],
          InvoiceManagement: ["invoiceData", "invoices"],
        };

        const keysToInvalidate = modelToQueryKey[data.model];
        if (keysToInvalidate) {
          keysToInvalidate.forEach((key) => {
            queryClient.invalidateQueries({ queryKey: [key] });
          });
        }
      });

      socket.on("user-login", (data: { id: string }) => {
        console.log("👤 User logged in:", data);
        const activeElement = document.activeElement;
        const isTyping = activeElement && ["INPUT", "TEXTAREA", "SELECT"].includes(activeElement.tagName);

        const hasActiveForm = Array.from(document.querySelectorAll("input, textarea, select")).some(el => {
          const tagName = el.tagName;
          const type = el.getAttribute("type");
          const placeholder = el.getAttribute("placeholder")?.toLowerCase() || "";
          const id = el.id?.toLowerCase() || "";
          const name = el.getAttribute("name")?.toLowerCase() || "";
          const className = el.className?.toLowerCase() || "";

          // Ignore checkboxes, radio buttons, hidden inputs, files, buttons
          const isIgnoredInput = type && ["checkbox", "radio", "hidden", "file", "button", "submit", "reset"].includes(type);
          if (isIgnoredInput) return false;

          // Ignore search fields
          const isSearch = type === "search" ||
            placeholder.includes("search") ||
            id.includes("search") ||
            name.includes("search") ||
            className.includes("search");
          if (isSearch) return false;

          // Ignore pagination selects / limit selectors
          const isPagination = tagName === "SELECT" && (
            name.includes("limit") ||
            name.includes("page") ||
            name.includes("size") ||
            id.includes("limit") ||
            id.includes("page") ||
            id.includes("size") ||
            className.includes("limit") ||
            className.includes("page") ||
            className.includes("size")
          );
          if (isPagination) return false;

          // Ignore filter & sort selectors
          const isFilterOrSort = name.includes("filter") ||
            name.includes("sort") ||
            id.includes("filter") ||
            id.includes("sort") ||
            className.includes("filter") ||
            className.includes("sort") ||
            placeholder.includes("filter") ||
            placeholder.includes("sort");
          if (isFilterOrSort) return false;

          // Ignore disabled / readonly
          const isDisabled = el.hasAttribute("disabled") || el.hasAttribute("readonly");
          if (isDisabled) return false;

          // Check if it is inside a form or a modal/sidebar/drawer/popup/overlay container
          let parent = el.parentElement;
          let insideFormOrModal = false;
          while (parent) {
            const pTagName = parent.tagName;
            const pRole = parent.getAttribute("role") || "";
            const pId = parent.id || "";
            const pClassName = typeof parent.className === "string" ? parent.className : "";

            if (pTagName === "FORM" || pTagName === "DIALOG") {
              insideFormOrModal = true;
              break;
            }
            if (pRole === "dialog" || pRole === "alertdialog") {
              insideFormOrModal = true;
              break;
            }
            const lowerId = pId.toLowerCase();
            const lowerClass = pClassName.toLowerCase();
            if (
              lowerId.includes("modal") || lowerId.includes("dialog") || lowerId.includes("drawer") || lowerId.includes("sidebar") || lowerId.includes("popup") ||
              lowerClass.includes("modal") || lowerClass.includes("dialog") || lowerClass.includes("drawer") || lowerClass.includes("sidebar") || lowerClass.includes("popup") ||
              (lowerClass.includes("fixed") && lowerClass.includes("z-"))
            ) {
              insideFormOrModal = true;
              break;
            }
            parent = parent.parentElement;
          }

          if (!insideFormOrModal) return false;

          // If we reach here, it's an active text/number/email/select/textarea form field inside a form/modal
          return true;
        });

        const isEditingPath = /[\/(]edit|install|connect|add|create|fill-info/i.test(window.location.pathname);
        if (isTyping || hasActiveForm || isEditingPath) {
          return;
        }
        if (data.id !== user?.id) {
          queryClient.invalidateQueries();
        }
      });

      return () => {
        socket.off("connect");
        socket.off("disconnect");
        socket.off("location received");
        socket.off("new-order-status");
        socket.off("notification Received");
        socket.off("backend-change");
        socket.off("user-login");
      };
    }
  }, [user?.id]);


  if (!isSuccess || data === undefined) {
    return <LoadingScreen />;
  }

  return (
    <div className="bg-gradient-to-tl from-20% to-[#E4E5E7] from-[#F9ECB7] h-screen overflow-hidden overflow-y-auto">
      {!hideHeader && <Header notification={unseenNotificationCount} />}
      <Toaster position="top-center" />
      <GlobalTooltipPortal />

      <Routes>
        {isAuthenticated ? (
          <>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/orders" element={<OrderManagement />} />
            <Route path="/orders/:awb" element={<ViewOrderDetails />} />
            <Route
              path="/shipment-details/:id"
              element={<ViewShipmentDetails />}
            />
            <Route path="/location" element={<Location />} />
            <Route path="/zone-management" element={<ZoneManagement />} />
            <Route path="/manifest" element={<ManifestManagement />} />
            <Route
              path="/inventory-mangement"
              element={<InverntoryProduct />}
            />
            <Route path="/product-demo" element={<ProductDemo />} />
            <Route path="/notifications" element={<Notification />} />
            <Route path="/notification" element={<Notification />} />
            <Route path="/notification-view" element={<NotificationView />} />
            <Route path="/add-item" element={<AddItem />} />
            <Route path="/add-item/:id" element={<AddItem />} />
            <Route path="/help-and-support" element={<HelpandSupport />} />
            <Route path="/invoice-management" element={<InvoiceManagement />} />
            <Route path="/integration" element={<Integration />} />
            <Route path="/settings" element={<Settings />} />
            <Route
              path="/barcode-qr-generator"
              element={<BarcodeQrGenerator />}
            />
            <Route path="/webhooks" element={<WebhooksPage />} />
            <Route path="/webhook" element={<Navigate to="/webhooks" replace />} />

            <Route path="/profile" element={<Profile />} />
            <Route
              path="/line-haul-management"
              element={<LineHaulManagement />}
            />
            {/* <Route path="/create-warehouse" element={<CreateWarehouse />} /> */}

            {/* fallback for unknown routes */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </>
        ) : (
          <>
            <Route path="/" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/password-changed" element={<ChangePassword />} />

            {/* fallback for unknown routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
